import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PaletteOutlined from "@mui/icons-material/PaletteOutlined";
import TrendingUpOutlined from "@mui/icons-material/TrendingUpOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import LocalFloristOutlined from "@mui/icons-material/LocalFloristOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import HandshakeOutlined from "@mui/icons-material/HandshakeOutlined";
import SavingsOutlined from "@mui/icons-material/SavingsOutlined";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import ExtensionOutlined from "@mui/icons-material/ExtensionOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import LanguageOutlined from "@mui/icons-material/LanguageOutlined";
import GrassOutlined from "@mui/icons-material/GrassOutlined";
import { try_for_free } from "../../url";
import { useCurrency } from "../../currency/CurrencyContext";
import requireSign_video from "../../Assets/requireSign_video.mp4";
import RefSiteFooter from "./RefSiteFooter";
import RefPricingTeaser from "./RefPricingTeaser";

const HOME_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
  :root {
    --rs-ink: #163A5A; --rs-ink2: #3D6A8C; --rs-ink3: #5E87A3;
    --rs-sky: #1A63A5; --rs-mint: #2AB6E8;
    --rs-secondary: #2AB6E8;
    --rs-secondary-deep: #1AA3D4;
    --rs-secondary-soft: #D9F4FC;
    --rs-secondary-muted: rgba(42,182,232,0.18);
    --rs-sky-light: #E8F4FC;
    --rs-sky-hover: #155a94;
    --rs-bg-dark: #0E2D48; --rs-bg-dark-hover: #0F3554; --rs-bg-dark-line: rgba(42,182,232,.22);
    --rs-white: #FFFFFF; --rs-surface: #F5F9FC;
    --rs-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif; --rs-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
  }
  .rsh-page { font-family: var(--rs-ff-body); color: var(--rs-ink); background: var(--rs-white); }
  .rsh-hero-wrap { overflow: hidden; width: 100%; }
  .rsh-reveal {
    opacity: 0;
    transform: translateY(26px);
    transition: opacity 0.65s ease, transform 0.65s ease;
    will-change: opacity, transform;
  }
  .rsh-reveal.is-visible {
    opacity: 1;
    transform: translateY(0);
  }
  .rsh-reveal[data-reveal-delay="1"] { transition-delay: 0.08s; }
  .rsh-reveal[data-reveal-delay="2"] { transition-delay: 0.16s; }
  .rsh-reveal[data-reveal-delay="3"] { transition-delay: 0.24s; }

  /* HERO */
  .rsh-hero {
    padding: 80px 24px 64px; display: grid;
    grid-template-columns: 1fr minmax(500px, 700px); gap: 48px; align-items: center;
    max-width: 1320px; margin: 0 auto; position: relative;
    box-sizing: border-box; width: 100%;
  }
  .rsh-hero > * { min-width: 0; }
  .rsh-hero-bg {
    position: absolute; top: -80px; right: -100px;
    width: 600px; height: 600px; border-radius: 50%;
    background: radial-gradient(circle, rgba(26,99,165,.07) 0%, transparent 70%);
    pointer-events: none;
  }
  .rsh-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(135deg, var(--rs-sky-light) 0%, var(--rs-secondary-soft) 100%);
    color: var(--rs-sky); border: 1px solid var(--rs-secondary-muted);
    font-size: 12px; font-weight: 600; padding: 6px 14px;
    border-radius: 100px; margin-bottom: 24px; letter-spacing: .04em;
    max-width: 100%; box-sizing: border-box; white-space: normal; text-align: left;
    overflow: hidden; word-break: break-word;
  }
  .rsh-eyebrow-dot {
    width: 6px; height: 6px; border-radius: 50%; background: var(--rs-secondary);
    animation: rsh-pulse 2s infinite; flex-shrink: 0;
  }
  @keyframes rsh-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(0.8)} }
  .rsh-h1 {
    font-family: var(--rs-ff-head); font-weight: 800; font-size: clamp(36px, 5.5vw, 62px);
    line-height: 1.05; letter-spacing: -.02em; color: var(--rs-ink); margin-bottom: 24px;
    text-transform: none;
  }
  .rsh-h1-grad {
    background: linear-gradient(135deg, var(--rs-sky) 0%, var(--rs-mint) 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .rsh-hero-sub {
    font-size: 17px; color: var(--rs-ink2); line-height: 1.65;
    max-width: 520px; margin-bottom: 36px; font-weight: 400;
    word-wrap: break-word; overflow-wrap: break-word;
  }
  .rsh-badges { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 36px; }
  .rsh-badge { display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 500; color: var(--rs-ink2); }
  .rsh-badge:hover { color: var(--rs-sky); transform: translateY(-2px); }
  .rsh-badge-icon {
    width: 28px; height: 28px; border-radius: 8px;
    background: var(--rs-sky-light); border: 1px solid rgba(26,99,165,.18);
    display: flex; align-items: center; justify-content: center;
    color: var(--rs-sky); flex-shrink: 0;
  }
  .rsh-badge-icon .MuiSvgIcon-root { font-size: 17px; }
  .rsh-cta-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
  .rsh-btn {
    font-family: var(--rs-ff-body); font-size: 14.5px; font-weight: 600;
    padding: 13px 26px; border-radius: 10px; cursor: pointer;
    transition: all .2s; border: none; text-decoration: none; display: inline-flex; align-items: center;
    white-space: nowrap;
  }
  .rsh-btn-primary { background: linear-gradient(135deg, var(--rs-sky) 0%, var(--rs-secondary-deep) 100%); color: #fff; box-shadow: 0 4px 14px rgba(42,182,232,0.35); }
  .rsh-btn-primary:hover { background: linear-gradient(135deg, var(--rs-sky-hover) 0%, var(--rs-sky) 100%); transform: translateY(-2px); box-shadow: 0 6px 18px rgba(42,182,232,0.4); }
  .rsh-btn-link { background: transparent; color: var(--rs-sky); font-size: 14px; font-weight: 600; }
  .rsh-btn-link:hover { color: var(--rs-sky-hover); }
  .rsh-btn:focus-visible, .rsh-video-chip:focus-visible {
    outline: 2px solid rgba(26,99,165,.5);
    outline-offset: 2px;
  }

  /* HERO CARD */
  .rsh-card {
    background: var(--rs-white); border: 1px solid rgba(26,99,165,.12); border-radius: 20px;
    padding: 28px; box-shadow: 0 20px 60px rgba(22,58,90,.1);
    transition: transform .25s ease, box-shadow .25s ease;
  }
  .rsh-card:hover { transform: translateY(-4px); box-shadow: 0 28px 68px rgba(22,58,90,.16); }
  .rsh-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; gap: 8px; flex-wrap: wrap; }
  .rsh-card-title { font-family: var(--rs-ff-head); font-size: 14px; font-weight: 600; color: var(--rs-ink); }
  .rsh-status-pill {
    font-size: 11px; font-weight: 500; background: #ECFDF5; color: #059669;
    padding: 4px 10px; border-radius: 100px; border: 1px solid #A7F3D0;
    white-space: nowrap;
  }
  .rsh-doc-item {
    background: var(--rs-surface); border-radius: 12px; padding: 14px 16px;
    margin-bottom: 10px; display: flex; align-items: center; gap: 12px;
    transition: transform .2s ease, background-color .2s ease;
  }
  .rsh-doc-item:hover { transform: translateX(4px); background: #eef6fc; }
  .rsh-doc-icon {
    width: 36px; height: 36px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .rsh-doc-icon .MuiSvgIcon-root { font-size: 20px; }
  .rsh-doc-icon-blue { background: #DCEEF9; color: var(--rs-sky); }
  .rsh-doc-icon-green { background: #E8F6F0; color: #0D7A54; }
  .rsh-doc-icon-amber { background: #FEF6E7; color: #B45309; }
  .rsh-doc-meta { flex: 1; min-width: 0; }
  .rsh-doc-name { font-size: 13px; font-weight: 500; color: var(--rs-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .rsh-doc-sub { font-size: 11px; color: var(--rs-ink3); margin-top: 2px; }
  .rsh-sig-badge { font-size: 11px; font-weight: 500; padding: 3px 9px; border-radius: 6px; white-space: nowrap; }
  .rsh-badge-signed { background: #ECFDF5; color: #059669; }
  .rsh-badge-pending { background: #FFF7ED; color: #D97706; }
  .rsh-badge-waiting { background: #E3F5FC; color: var(--rs-sky); }
  .rsh-stats-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-top: 16px; }
  .rsh-stat { 
    background: var(--rs-surface); 
    border-radius: 10px; 
    padding: 12px 8px; 
    text-align: center; 
    transition: transform .2s ease;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 80px;
  }
  .rsh-stat:hover { transform: translateY(-2px); }
  .rsh-stat-num { font-family: var(--rs-ff-head); font-size: 20px; font-weight: 700; color: var(--rs-sky); line-height: 1.1; }
  .rsh-stat-num.rsh-stat-muted { color: var(--rs-ink); }
  .rsh-stat-num-infinity { font-size: 38px; line-height: 1; margin: 2px 0; }
  @media (max-width: 600px) {
    .rsh-stat-num-infinity { font-size: 28px; }
  }
  .rsh-stat-lbl { font-size: 9px; font-weight: 600; color: var(--rs-ink3); margin-top: 4px; letter-spacing: .05em; line-height: 1.2; text-transform: uppercase; word-break: break-word; }
  .rsh-stat-prefix { font-size: 9px; font-weight: 600; color: var(--rs-ink3); text-transform: uppercase; letter-spacing: .05em; margin-bottom: 4px; }

  /* VIDEO PLAYER */
  .rsh-video-shell {
    position: relative;
    width: 100%;
    justify-self: end;
    cursor: pointer;
  }
  .rsh-video-wrapper {
    position: relative; z-index: 1; padding: 12px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%);
    border-radius: 24px; backdrop-filter: blur(10px); border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 20px 60px rgba(22,58,90,.15); width: 100%; max-width: 700px;
    box-sizing: border-box;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }
  .rsh-video-shell:hover .rsh-video-wrapper,
  .rsh-video-shell:focus-within .rsh-video-wrapper {
    transform: translateY(-4px);
    box-shadow: 0 30px 75px rgba(22,58,90,.22);
  }
  .rsh-video-player {
    width: 100%; height: auto; display: block; border-radius: 16px;
    box-shadow: 0 12px 32px rgba(0,0,0,0.15); transition: transform 0.4s ease;
    object-fit: cover; aspect-ratio: 16 / 9;
  }
  .rsh-video-player:hover { transform: scale(1.02); }

  /* VALUE SECTION */
  .rsh-value-section {
    padding: 56px 24px 40px; max-width: 1240px; margin: 0 auto; box-sizing: border-box;
    display: grid; grid-template-columns: 1fr; gap: 12px; align-items: center;
    width: 100%;
  }
  .rsh-value-content { text-align: left; order: 2; }
  .rsh-value-main-row .rsh-card { order: 1; }
  .rsh-value-main-row {
    display: grid;
    grid-template-columns: 1fr 440px;
    gap: 56px;
    align-items: center;
  }
  .rsh-value-list-row {
    margin-top: 12px;
  }
  .rsh-section-label { font-size: 11px; font-weight: 700; color: var(--rs-secondary); letter-spacing: .1em; margin-bottom: 12px; text-transform: uppercase; }
  .rsh-h2 {
    font-family: var(--rs-ff-head); font-size: clamp(32px, 4.5vw, 48px); font-weight: 800;
    line-height: 1.1; letter-spacing: -.02em; color: var(--rs-ink); margin-bottom: 32px;
  }
  .rsh-value-list {
    display: grid;
    grid-template-columns: repeat(5, minmax(110px, 1fr));
    gap: 14px;
    margin-top: 32px;
  }
  .rsh-value-item {
    background: var(--rs-white);
    border: 1px solid rgba(26,99,165,.14);
    border-radius: 16px;
    aspect-ratio: 1 / 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    gap: 10px;
    padding: 12px;
    box-shadow: 0 10px 30px rgba(22,58,90,.08);
    transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
  }
  .rsh-value-item:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 34px rgba(22,58,90,.14);
    border-color: rgba(42,182,232,.5);
  }
  .rsh-value-icon-box {
    width: 52px; height: 52px; border-radius: 14px; background: var(--rs-sky-light);
    display: flex; align-items: center; justify-content: center; color: var(--rs-sky);
    border: 1px solid rgba(26,99,165,.1); flex-shrink: 0;
  }
  .rsh-value-icon-box .MuiSvgIcon-root { font-size: 26px; }
  .rsh-value-name {
    font-family: var(--rs-ff-head); font-size: 12.5px; font-weight: 700; color: var(--rs-ink);
    line-height: 1.3; min-height: 2.6em;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
  }
  .rsh-value-name-line { display: block; word-break: break-word; }

  /* FEATURES STRIP */
  .rsh-feat-strip { background: var(--rs-bg-dark); padding: 80px 40px; }
  .rsh-feat-inner { max-width: 1240px; margin: 0 auto; }
  .rsh-feat-strip .rsh-section-label { color: var(--rs-mint); }
  .rsh-feat-strip .rsh-h2 { color: #fff; margin-bottom: 12px; }
  .rsh-feat-sub { font-size: 16px; color: rgba(255,255,255,.6); font-weight: 400; max-width: 540px; line-height: 1.6; margin-bottom: 48px; }
  .rsh-feat-grid {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 1px; background: var(--rs-bg-dark-line); border-radius: 16px; overflow: hidden;
    border: 1px solid var(--rs-bg-dark-line);
  }
  .rsh-feat-item { background: var(--rs-bg-dark); padding: 32px; transition: background .2s; }
  .rsh-feat-item:hover { background: var(--rs-bg-dark-hover); transform: translateY(-2px); }
  .rsh-feat-num { font-family: var(--rs-ff-head); font-size: 11px; font-weight: 600; color: rgba(125,211,252,.45); letter-spacing: .08em; margin-bottom: 16px; }
  .rsh-feat-icon {
    width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
    margin-bottom: 16px;
  }
  .rsh-feat-icon .MuiSvgIcon-root { font-size: 22px; }
  .rsh-fi-blue { background: rgba(26,99,165,.28); color: #93C5FD; }
  .rsh-fi-mint { background: rgba(42,182,232,.22); color: #BAE6FD; }
  .rsh-fi-purple { background: rgba(96,165,250,.18); color: #C4B5FD; }
  .rsh-fi-amber { background: rgba(251,191,36,.2); color: #FCD34D; }
  .rsh-fi-coral { background: rgba(251,146,60,.2); color: #FDBA74; }
  .rsh-fi-teal { background: rgba(45,212,191,.18); color: #5EEAD4; }
  .rsh-fi-amber .MuiSvgIcon-root, .rsh-fi-coral .MuiSvgIcon-root, .rsh-fi-teal .MuiSvgIcon-root { color: inherit; }
  .rsh-feat-title { font-family: var(--rs-ff-head); font-size: 17px; font-weight: 700; color: #fff; margin-bottom: 8px; }
  .rsh-feat-desc { font-size: 13px; color: rgba(255,255,255,.55); line-height: 1.6; }

  @media (max-width: 1024px) {
    .rsh-hero { grid-template-columns: 1fr; padding: 48px 24px; gap: 40px; }
    .rsh-value-section { grid-template-columns: 1fr; padding: 48px 24px; gap: 20px; }
    .rsh-hero-bg { display: none; }
    .rsh-h1 { font-size: 44px; }
    .rsh-hero-sub { max-width: 100%; }
    .rsh-card { width: 100%; box-sizing: border-box; }
    .rsh-video-shell { justify-self: stretch; }
    .rsh-video-wrapper { max-width: 100%; }
    .rsh-value-content { order: 1; }
    .rsh-value-main-row .rsh-card { order: 2; }
    .rsh-value-main-row { grid-template-columns: 1fr; gap: 28px; }
    .rsh-value-list-row { margin-top: 0; }
    .rsh-value-list { grid-template-columns: repeat(3, minmax(120px, 1fr)); margin-bottom: 40px; }
    .rsh-feat-grid { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 800px) {
    .rsh-value-list { grid-template-columns: repeat(2, minmax(120px, 1fr)); }
    .rsh-feat-strip { padding: 64px 24px; }
  }
  @media (max-width: 600px) {
    .rsh-hero { padding: 40px 20px 48px; gap: 32px; }
    .rsh-value-list { grid-template-columns: repeat(2, minmax(120px, 1fr)); gap: 12px; }
    .rsh-feat-grid { grid-template-columns: 1fr; }
    .rsh-h1 { font-size: clamp(28px, 8.5vw, 38px); line-height: 1.1; }
    .rsh-hero-sub { font-size: 15px; margin-bottom: 32px; max-width: 100%; }
    .rsh-cta-row { flex-wrap: wrap; justify-content: flex-start; gap: 12px; }
    .rsh-btn { width: 100%; justify-content: center; box-sizing: border-box; }
    .rsh-btn-link { width: auto; }
    .rsh-value-section, .rsh-feat-strip { padding: 40px 20px; }
    .rsh-badges { gap: 10px; }
    .rsh-badge { font-size: 11px; }
    .rsh-eyebrow { font-size: 11px; padding: 6px 12px; max-width: 100%; }
    .rsh-stats-row { grid-template-columns: repeat(3, 1fr); gap: 6px; }
    .rsh-card { padding: 20px; box-sizing: border-box; }
    .rsh-doc-item { padding: 10px 12px; gap: 10px; }
  }
  @media (max-width: 400px) {
    .rsh-hero { padding: 32px 16px 40px; }
    .rsh-h1 { font-size: 26px; }
    .rsh-card { padding: 16px; }
    .rsh-doc-name { font-size: 12px; }
    .rsh-sig-badge { padding: 2px 6px; font-size: 10px; }
    .rsh-stats-row { grid-template-columns: repeat(3, 1fr); gap: 4px; }
    .rsh-stat-num { font-size: 15px; }
    .rsh-stat-lbl { font-size: 7.5px; }
    .rsh-value-section, .rsh-feat-strip { padding: 34px 16px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .rsh-reveal, .rsh-btn, .rsh-card, .rsh-doc-item, .rsh-stat, .rsh-value-item, .rsh-video-wrapper, .rsh-video-player, .rsh-feat-item {
      transition: none !important;
      animation: none !important;
    }
    .rsh-reveal { opacity: 1; transform: none; }
  }
`;

const DashboardCard = () => {
  const { t } = useTranslation('home');
  const { format } = useCurrency();

  return (
    <div className="rsh-card">
      <div className="rsh-card-header">
        <span className="rsh-card-title">{t('dashboard.title')}</span>
        <span className="rsh-status-pill">{t('dashboard.status')}</span>
      </div>
      {[
        {
          Icon: DescriptionOutlined,
          cls: "rsh-doc-icon-blue",
          name: t('dashboard.docs.nda.name'),
          sub: t('dashboard.docs.nda.sub'),
          badge: t('dashboard.docs.nda.badge'),
          badgeCls: "rsh-badge-signed",
        },
        {
          Icon: AssignmentOutlined,
          cls: "rsh-doc-icon-amber",
          name: t('dashboard.docs.service.name'),
          sub: t('dashboard.docs.service.sub'),
          badge: t('dashboard.docs.service.badge'),
          badgeCls: "rsh-badge-pending",
        },
        {
          Icon: HandshakeOutlined,
          cls: "rsh-doc-icon-green",
          name: t('dashboard.docs.mou.name'),
          sub: t('dashboard.docs.mou.sub'),
          badge: t('dashboard.docs.mou.badge'),
          badgeCls: "rsh-badge-waiting",
        },
      ].map((doc) => {
        const DocIcon = doc.Icon;
        return (
          <div className="rsh-doc-item" key={doc.name}>
            <div className={`rsh-doc-icon ${doc.cls}`}>
              <DocIcon />
            </div>
            <div className="rsh-doc-meta">
              <div className="rsh-doc-name">{doc.name}</div>
              <div className="rsh-doc-sub">{doc.sub}</div>
            </div>
            <span className={`rsh-sig-badge ${doc.badgeCls}`}>
              {doc.badge}
            </span>
          </div>
        );
      })}
      <div className="rsh-stats-row">
        {[
          { num: "∞", label: t('dashboard.stats.documents'), tone: "muted", special: true },
          {
            num: format(7.42),
            label: t('dashboard.stats.pricingSub', { price: format(89) }),
            tone: "sky",
            prefix: t('dashboard.stats.asLowAs')
          },
          { num: "100%", label: t('dashboard.stats.legal'), tone: "muted" },
        ].map((s) => (
          <div className="rsh-stat" key={s.label}>
            {s.prefix && <div className="rsh-stat-prefix">{s.prefix}</div>}
            <div
              className={`rsh-stat-num${s.tone === "muted" ? " rsh-stat-muted" : ""}${s.special ? " rsh-stat-num-infinity" : ""}`}
            >
              {s.num}
            </div>
            <div className="rsh-stat-lbl">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

function Home() {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const videoRef = useRef(null);

  useEffect(() => {
    const revealNodes = document.querySelectorAll("[data-reveal]");
    if (!revealNodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );

    revealNodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="rsh-page">
      <style>{HOME_STYLES}</style>

      {/* ── HERO ── */}
      <div
        className="rsh-hero-wrap rsh-reveal is-visible"
        data-reveal
        style={{
          background:
            "linear-gradient(180deg,#E8F4FC 0%,var(--rs-secondary-soft) 42%,#fff 100%)",
        }}
      >
        <div className="rsh-hero">
          <div className="rsh-hero-bg" />
          <div style={{ position: "relative" }}>
            <div className="rsh-eyebrow">
              <span className="rsh-eyebrow-dot" />
              {t('hero.eyebrow')}
            </div>
            <h1 className="rsh-h1">
              {t('hero.titleLine1')}
              <br />
              {t('hero.titleLine2')}
              <br />
              {t('hero.titleLine3Prefix')}<span className="rsh-h1-grad">{t('hero.titleLine3Highlight')}</span>
            </h1>
            <p className="rsh-hero-sub">
              {t('hero.subtitle')}
            </p>
            <div className="rsh-badges">
              {[
                { Icon: PaletteOutlined, label: t('hero.badges.rebrandable') },
                { Icon: TrendingUpOutlined, label: t('hero.badges.scalable') },
                { Icon: LockOutlined, label: t('hero.badges.secure') },
                { Icon: LocalFloristOutlined, label: t('hero.badges.ecoFriendly') },
              ].map(({ Icon, label }) => (
                <div className="rsh-badge" key={label}>
                  <span className="rsh-badge-icon">
                    <Icon />
                  </span>{" "}
                  {label}
                </div>
              ))}
            </div>
            <div className="rsh-cta-row">
              <a
                href={try_for_free}
                target="_blank"
                rel="noreferrer"
                className="rsh-btn rsh-btn-primary"
              >
                {t('hero.startTrial')}
              </a>
              <button
                className="rsh-btn rsh-btn-link"
                onClick={() => navigate("/pricing")}
              >
                {t('hero.seePricing')}
              </button>
            </div>
          </div>

          <div className="rsh-video-shell">
            <div className="rsh-video-wrapper">
              <video
                ref={videoRef}
                className="rsh-video-player"
                controls
                autoPlay
                muted
                loop
                playsInline
              >
                <source src={requireSign_video} type="video/mp4" />
                {t('hero.videoFallback')}
              </video>
            </div>
          </div>
        </div>
      </div>

      {/* ── VALUE CARDS ── */}
      <div className="rsh-value-section rsh-reveal" data-reveal data-reveal-delay="1">
        <div className="rsh-value-main-row">
          <div className="rsh-value-content">
            <div className="rsh-section-label">{t('value.label')}</div>
            <div className="rsh-h2">
              {t('value.titleLine1')}
              <br />
              {t('value.titleLine2')}
            </div>
          </div>
          <DashboardCard />
        </div>

        <div className="rsh-value-list-row rsh-reveal" data-reveal data-reveal-delay="2">
          <div className="rsh-value-list">
            {[
              { Icon: SavingsOutlined, line1: t('value.items.item1.line1'), line2: t('value.items.item1.line2') },
              { Icon: ShieldOutlined, line1: t('value.items.item2.line1'), line2: t('value.items.item2.line2') },
              { Icon: GavelOutlined, line1: t('value.items.item3.line1'), line2: t('value.items.item3.line2') },
              { Icon: ExtensionOutlined, line1: t('value.items.item4.line1'), line2: t('value.items.item4.line2') },
              { Icon: LocalFloristOutlined, line1: t('value.items.item5.line1'), line2: t('value.items.item5.line2') },
            ].map(({ Icon, line1, line2 }) => (
              <div className="rsh-value-item" key={`${line1}-${line2}`}>
                <div className="rsh-value-icon-box">
                  <Icon />
                </div>
                <div className="rsh-value-name">
                  <span className="rsh-value-name-line">{line1}</span>
                  <span className="rsh-value-name-line">{line2}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FEATURES STRIP ── */}
      <div className="rsh-feat-strip rsh-reveal" data-reveal data-reveal-delay="1">
        <div className="rsh-feat-inner">
          <div className="rsh-section-label">{t('features.label')}</div>
          <div className="rsh-h2">
            {t('features.titleLine1')}
            <br />
            {t('features.titleLine2')}
          </div>
          <p className="rsh-feat-sub">
            {t('features.subtitle')}
          </p>
          <div className="rsh-feat-grid">
            {[
              {
                n: "01",
                Icon: EditOutlined,
                cls: "rsh-fi-blue",
                title: t('features.items.unlimited.title'),
                desc: t('features.items.unlimited.desc'),
              },
              {
                n: "02",
                Icon: PaletteOutlined,
                cls: "rsh-fi-mint",
                title: t('features.items.whiteLabel.title'),
                desc: t('features.items.whiteLabel.desc'),
              },
              {
                n: "03",
                Icon: GavelOutlined,
                cls: "rsh-fi-purple",
                title: t('features.items.legal.title'),
                desc: t('features.items.legal.desc'),
              },
              {
                n: "04",
                Icon: VerifiedUserOutlined,
                cls: "rsh-fi-amber",
                title: t('features.items.security.title'),
                desc: t('features.items.security.desc'),
              },
              {
                n: "05",
                Icon: LanguageOutlined,
                cls: "rsh-fi-coral",
                title: t('features.items.global.title'),
                desc: t('features.items.global.desc'),
              },
              {
                n: "06",
                Icon: GrassOutlined,
                cls: "rsh-fi-teal",
                title: t('features.items.paperless.title'),
                desc: t('features.items.paperless.desc'),
              },
            ].map((f) => {
              const FeatIcon = f.Icon;
              return (
                <div className="rsh-feat-item" key={f.n}>
                  <div className="rsh-feat-num">{f.n}</div>
                  <div className={`rsh-feat-icon ${f.cls}`}>
                    <FeatIcon />
                  </div>
                  <div className="rsh-feat-title">{f.title}</div>
                  <div className="rsh-feat-desc">{f.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── PRICING TEASER ── */}
      <RefPricingTeaser />

      {/* ── FOOTER ── */}
      <RefSiteFooter />
    </div>
  );
}

export default Home;
